/**
 * Campo de texto multilínea para formularios. Comparte la misma base visual que `Input`
 * (`bg-surface-muted border border-edge rounded-lg`) y acepta todas las props nativas de `<textarea>`.
 *
 * @param {string}  [props.error]       - Mensaje de error; cambia el borde a rojo.
 * @param {string}  [props.helperText]  - Texto de ayuda bajo el campo (ignorado si hay `error`).
 * @param {number}  [props.rows=3]      - Altura en líneas.
 * @param {number}  [props.maxLength]   - Límite de caracteres. Si se pasa y el componente es
 *                                        controlado (`value`), muestra un contador.
 * @param {string}  [props.className]   - Clases extra que se agregan al `<textarea>`.
 * @param {string}  [props.id]          - ID del campo (opcional).
 *
 * Scroll vertical: \`overflow-y-auto\` + \`scrollbar-aimsos-auto\` (plugin Tailwind del paquete).
 */
const TextArea = ({
  error,
  helperText,
  id,
  rows = 3,
  maxLength,
  value,
  className = '',
  ...props
}) => {
  const charCount = maxLength != null && typeof value === 'string' ? value.length : null;
  const hasFooter = error || helperText || charCount != null;

  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        id={id}
        rows={rows}
        maxLength={maxLength}
        value={value}
        className={[
          'w-full px-3 py-2 text-sm bg-surface-muted border border-edge rounded-lg',
          'text-content placeholder:text-content-faint',
          'outline-none transition-colors hover:border-accent/50 focus:border-accent/50',
          'resize-none overflow-y-auto scrollbar-aimsos-sm',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error ? 'border-red-400 hover:border-red-400 focus:border-red-400' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      />

      {hasFooter ? (
        <div className="flex items-start justify-between gap-2">
          {error ? (
            <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
          ) : helperText ? (
            <p className="text-xs text-content-muted">{helperText}</p>
          ) : (
            <span />
          )}

          {charCount != null ? (
            <p
              className={[
                'shrink-0 text-xs tabular-nums',
                charCount >= maxLength ? 'text-red-500 dark:text-red-400' : 'text-content-muted',
              ].join(' ')}
            >
              {charCount}/{maxLength}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default TextArea;
